import { Form } from 'antd';
import { SeedTicket } from 'constants/interface';
import { buildRegularProductDisplay, buildSimpleQuantityDisplay } from 'helpers/order';
import React, { useState } from 'react';
import { useDispatch } from 'hooks/store';
import {
  DatePicker,
  Input,
  InputNumber,
  Modal,
} from 'UI';
import { confirmSeedTicketAction } from 'state/slices/productionItem';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { unwrapResult } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';

import {
  InlineButton,
  MainContainer,
  FormContainer,
  ItemInfoContainer,
  ItemInfoRow,
  ItemInfoLabel,
  ItemInfoValue,
  BaseFormItem,
  FormItemRow,
} from './style';

interface ConfirmTicketModalProps {
  item?: SeedTicket
  isVisible: boolean
  onCancel: () => void
  onSuccess: () => void
}

interface ConfirmTicketFormData {
  purchaseDate: Dayjs
  provider: string
  purchaseQuantity: string
  estimatedReceivingDate: Dayjs
}

const ConfirmTicketModal = ({
  item,
  isVisible,
  onCancel,
  onSuccess,
}: ConfirmTicketModalProps): JSX.Element => {
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(false);

  const [confirmTicketForm] = Form.useForm<ConfirmTicketFormData>();

  const confirmSeedTicket = async (): Promise<void> => {
    try {
      await confirmTicketForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification(
        'Verifique os campos com erro.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      return;
    }
    const formData = confirmTicketForm.getFieldsValue();

    const parsedData = {
      purchaseDate: formData.purchaseDate.format(),
      provider: formData.provider,
      purchaseQuantity: parseInt(formData.purchaseQuantity, 10),
      estimatedReceivingDate: formData.estimatedReceivingDate.format(),
    };

    try {
      setLoading(true);
      const result = await dispatch(confirmSeedTicketAction({
        id: item?.id || '',
        confirmationData: parsedData,
      }));
      setLoading(false);
      unwrapResult(result);
      onSuccess();
    } catch (err) {
      const errorAlert = createAlertNotification(
        'Erro interno ao tentar confirmar solicitação. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={isVisible}
      title="Confirmar solicitação"
      destroyOnClose
      onCancel={onCancel}
      afterClose={onCancel}
      zIndex={90}
      footer={[
        <InlineButton
          key="cancel"
          onClick={onCancel}
          type="ghost"
        >
          Cancelar
        </InlineButton>,
        <InlineButton
          key="submit"
          onClick={confirmSeedTicket}
          disabled={isLoading}
          loading={isLoading}
        >
          Salvar
        </InlineButton>,
      ]}
    >
      <MainContainer>
        <ItemInfoContainer>
          <ItemInfoRow>
            <ItemInfoLabel>Nº Solicitação:</ItemInfoLabel>
            <ItemInfoValue>{`${item?.seq || ''}`}</ItemInfoValue>
          </ItemInfoRow>
          <ItemInfoRow>
            <ItemInfoLabel>Cliente:</ItemInfoLabel>
            <ItemInfoValue>{`${item?.productionItem.orderItem.order.client.name || ''}`}</ItemInfoValue>
          </ItemInfoRow>
          <ItemInfoRow>
            <ItemInfoLabel>Cultivar:</ItemInfoLabel>
            <ItemInfoValue>
              {`${item ? buildRegularProductDisplay(item.productionItem, {
                quantity: false,
                tray: false,
              }) : ''}`}
            </ItemInfoValue>
          </ItemInfoRow>
          <ItemInfoRow>
            <ItemInfoLabel>Quantidade prevista:</ItemInfoLabel>
            <ItemInfoValue>
              {`${item ? buildSimpleQuantityDisplay(item.requestQuantity) : ''}`}
            </ItemInfoValue>
          </ItemInfoRow>
        </ItemInfoContainer>
        <FormContainer
          layout="vertical"
          form={confirmTicketForm}
          requiredMark={false}
          validateTrigger="onSubmit"
          // initialValues={{
          //   requestQuantity: item.calculatedQuantity,
          // }}
        >
          <FormItemRow>
            <BaseFormItem
              label="Data de compra"
              name="purchaseDate"
              rules={[
                {
                  required: true,
                  message: 'Preencha uma quantidade',
                },
              ]}
            >
              <DatePicker />
            </BaseFormItem>
          </FormItemRow>
          <FormItemRow>
            <BaseFormItem
              label="Fornecedor"
              name="provider"
              rules={[
                {
                  required: true,
                  message: 'Preencha um fornecedor',
                },
              ]}
            >
              <Input />
            </BaseFormItem>
          </FormItemRow>
          <FormItemRow>
            <BaseFormItem
              label="Quantidade solicitada"
              name="purchaseQuantity"
              rules={[
                {
                  required: true,
                  message: 'Preencha uma quantidade',
                },
              ]}
            >
              <InputNumber />
            </BaseFormItem>
          </FormItemRow>
          <FormItemRow>
            <BaseFormItem
              label="Previsão de entrega"
              name="estimatedReceivingDate"
              rules={[
                {
                  required: true,
                  message: 'Insira uma data',
                },
              ]}
            >
              <DatePicker />
            </BaseFormItem>
          </FormItemRow>
        </FormContainer>
      </MainContainer>
    </Modal>
  );
};

export default ConfirmTicketModal;
